import Phaser from 'phaser';
import { Depth, FONT_BODY, GAME_H, GAME_W } from '@/core/config';
import type { GameContext } from '@/core/context';
import { ensureAudioDirector } from './index';
import { AmbiencePresets } from './manifest';

const FONT = { fontFamily: FONT_BODY, fontSize: '11px', color: '#f1e6c8' };
const MUSIC = [['1', 'music-letter', '家书·回忆'], ['2', 'music-harbor', '绿色码头'], ['3', 'music-inn-rain', '雨中旅店'], ['4', 'music-sparring', '切磋'], ['5', 'music-omen', '悬念结尾'], ['0', null, '停止音乐']] as const;
const AMB = [['Q', 'harbor-sultry', '码头·闷热'], ['W', 'harbor-wind', '码头·起风'], ['E', 'harbor-drizzle', '码头·细雨'], ['R', 'harbor-rain', '码头·大雨'], ['T', 'inn-interior-rain', '旅店·雨中'], ['Y', 'omen-end', '不祥结尾'], ['U', 'silence', '静音层']] as const;

/** ?showcase=audio — exercises the real AudioDirector: unlock state, cues, ambience mixes, sfx bus, duck, volumes, spatial pan. */
export function audioShowcase(scene: Phaser.Scene): void {
  const director = ensureAudioDirector(scene.game);
  (window as unknown as { __nantianAudio?: unknown }).__nantianAudio = director;
  const ctx = scene.registry.get('ctx') as GameContext | undefined;
  scene.cameras.main.setBackgroundColor('#141a20');
  const objects: Phaser.GameObjects.GameObject[] = [];
  const add = <T extends Phaser.GameObjects.GameObject>(o: T): T => { objects.push(o); return o; };

  add(scene.add.text(8, 6, '音频 showcase — AudioDirector（Web Audio）  点击任意处解锁', { ...FONT, fontSize: '13px', color: '#e2c48c' }));

  const button = (x: number, y: number, w: number, label: string, onClick: () => void, hotkey?: string): Phaser.GameObjects.Text => {
    const bg = add(scene.add.rectangle(x, y, w, 16, 0x3a2a1e).setOrigin(0).setStrokeStyle(1, 0x8a5a32));
    const t = add(scene.add.text(x + 4, y + 2, hotkey ? `[${hotkey}] ${label}` : label, FONT));
    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setFillStyle(0x8c2a22)); bg.on('pointerout', () => bg.setFillStyle(0x3a2a1e));
    bg.on('pointerdown', () => { onClick(); flash(bg); });
    if (hotkey) scene.input.keyboard?.on(`keydown-${hotkey === '0' ? 'ZERO' : /\d/.test(hotkey) ? ['ZERO', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE'][Number(hotkey)] : hotkey}`, () => { onClick(); flash(bg); });
    return t;
  };
  const flash = (bg: Phaser.GameObjects.Rectangle) => { bg.setFillStyle(0xe2c48c); scene.time.delayedCall(120, () => bg.setFillStyle(0x3a2a1e)); };

  // music column
  add(scene.add.text(8, 26, '音乐（交叉淡入淡出）', { ...FONT, color: '#8ec95a' }));
  MUSIC.forEach(([k, key, label], i) => button(8, 40 + i * 19, 120, label, () => director.playMusic(key, 900), k));
  // ambience column
  add(scene.add.text(136, 26, '环境层（预设混合）', { ...FONT, color: '#8ec95a' }));
  AMB.forEach(([k, preset, label], i) => button(136, 40 + i * 19, 120, label, () => director.setAmbience(AmbiencePresets[preset] ?? [], 1200), k));
  // controls
  add(scene.add.text(264, 26, '控制', { ...FONT, color: '#8ec95a' }));
  const patch = (p: Partial<{ musicVolume: number; ambienceVolume: number; sfxVolume: number; muted: boolean }>) => {
    if (ctx) ctx.updateSettings(p); else { const s = director.getState().settings; director['applySettings']({ ...s, ...p } as never); }
  };
  const st = () => director.getState().settings;
  const clamp01 = (v: number) => Math.round(Math.max(0, Math.min(1, v)) * 10) / 10;
  button(264, 40, 100, '闪避 duck 0.7/1.5s', () => director.duck(0.7, 1500), 'D');
  button(264, 59, 100, '静音切换', () => patch({ muted: !st().muted }), 'K'); // K: 'M' is the UI pause-menu key
  button(264, 78, 48, '音乐 −', () => patch({ musicVolume: clamp01(st().musicVolume - 0.1) }));
  button(316, 78, 48, '音乐 +', () => patch({ musicVolume: clamp01(st().musicVolume + 0.1) }));
  button(264, 97, 48, '环境 −', () => patch({ ambienceVolume: clamp01(st().ambienceVolume - 0.1) }));
  button(316, 97, 48, '环境 +', () => patch({ ambienceVolume: clamp01(st().ambienceVolume + 0.1) }));
  button(264, 116, 48, '音效 −', () => patch({ sfxVolume: clamp01(st().sfxVolume - 0.1) }));
  button(316, 116, 48, '音效 +', () => patch({ sfxVolume: clamp01(st().sfxVolume + 0.1) }));
  button(264, 135, 100, '解锁 unlock()', () => void director.unlock());

  // sfx grid (from manifest when it arrives)
  add(scene.add.text(376, 26, '音效（点击播放）', { ...FONT, color: '#8ec95a' }));
  const sfxArea = { x: 376, y: 40, cols: 3, w: 84 };
  const sfxButtons: Phaser.GameObjects.GameObject[] = [];
  const buildSfx = () => {
    const m = director.getManifest(); if (!m) return;
    const keys = Object.entries(m.entries).filter(([, e]) => e.kind === 'sfx').map(([k]) => k);
    keys.forEach((k, i) => { const col = i % sfxArea.cols, row = Math.floor(i / sfxArea.cols); const t = button(sfxArea.x + col * (sfxArea.w + 4), sfxArea.y + row * 17, sfxArea.w, k.replace(/^sfx-/, ''), () => director.sfx(k)); t.setFontSize(9); sfxButtons.push(t); });
  };
  void director.loadManifest().then(() => { if (scene.scene.isActive()) buildSfx(); });

  // spatial pad: click plays a footstep at that offset from the listener (pad centre)
  const pad = { x: 8, y: 180, w: 248, h: 110 };
  add(scene.add.rectangle(pad.x, pad.y, pad.w, pad.h, 0x1f2f3a).setOrigin(0).setStrokeStyle(1, 0x6fd3c8)).setInteractive().on('pointerdown', (p: Phaser.Input.Pointer) => {
    const lx = pad.x + pad.w / 2, ly = pad.y + pad.h / 2;
    const wx = (p.x - lx) * 2, wy = (p.y - ly) * 2; // pad px → world px (2× so the pad spans ±248 px)
    director.setListener(0, 0);
    director.sfx('sfx-footstep-sand', { x: wx, y: wy });
    const dot = add(scene.add.rectangle(Math.round(p.x), Math.round(p.y), 3, 3, 0xffb347));
    scene.tweens.add({ targets: dot, alpha: 0, duration: 500, onComplete: () => dot.destroy() });
  });
  add(scene.add.text(pad.x + 4, pad.y + 2, '空间音效测试：点击垫子 → 相对听者（中心）的脚步声，左右声像/距离衰减', { ...FONT, fontSize: '10px', color: '#6fd3c8', wordWrap: { width: pad.w - 8 } }));
  add(scene.add.rectangle(pad.x + pad.w / 2, pad.y + pad.h / 2, 4, 4, 0xe2c48c));

  // live state
  const state = add(scene.add.text(264, 214, '', { ...FONT, fontSize: '10px', color: '#c9b89a', lineSpacing: 2, wordWrap: { width: GAME_W - 272 } }).setDepth(Depth.UI));
  const timer = scene.time.addEvent({ delay: 150, loop: true, callback: () => {
    const s = director.getState();
    state.setText([
      `unlocked: ${s.unlocked} (event ${s.unlockEmitted ? '✓' : '…'})   manager.locked: ${s.locked}   context: ${s.contextState}   manifest: ${s.manifestLoaded ? '✓' : '…'}`,
      `music: ${s.music ?? '—'}  (vol ${s.musicVolume.toFixed(2)})   duck: ${s.duck}`,
      `ambience: ${s.ambience.map((l) => `${l.key}@${l.volume}`).join('  ') || '—'}`,
      `settings: music ${s.settings.musicVolume}  amb ${s.settings.ambienceVolume}  sfx ${s.settings.sfxVolume}  muted ${s.settings.muted}`,
      `loaded: ${s.loaded.length} keys   failed(muted): ${s.failed.join(', ') || '—'}`,
    ].join('\n'));
  } });
  add(scene.add.text(8, GAME_H - 14, '快捷键：1-5/0 音乐  Q-U 环境  D 闪避  K 静音   Esc 返回标题', { ...FONT, fontSize: '10px', color: '#8a8a8a' }));
  scene.input.keyboard?.on('keydown-ESC', () => void ctx?.toTitle());

  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
    delete (window as unknown as { __nantianAudio?: unknown }).__nantianAudio;
    timer.remove(); scene.input.keyboard?.removeAllListeners(); scene.input.removeAllListeners();
    for (const o of objects) o.destroy();
    for (const b of sfxButtons) b.destroy();
  });
  void GAME_W;
}
